import React, { useEffect, useState, useCallback } from 'react';
import { AdminPageHead } from '../components/AdminPageHead';
import {
    Box, Card, CardContent, Typography, Grid, Table, TableHead,
    TableRow, TableCell, TableBody, TableContainer, CircularProgress,
    TextField, Button, Chip, Divider, Tooltip, IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AddShoppingCartOutlinedIcon from '@mui/icons-material/AddShoppingCartOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { PixelService } from '../../../../../core/services/pixel.service';

interface TotalRow { event_type: string; total: number; }
interface BrandRow {
    cctm_id: string;
    pixel_name: string;
    website_url: string;
    pageviews: number;
    add_to_cart: number;
    initiate_checkout: number;
    purchases: number;
    unique_users: number;
    total_events: number;
}

const EVENT_META: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
    pageview:          { label: 'Page Views',        color: '#5c6bc0', icon: <VisibilityOutlinedIcon fontSize="small" /> },
    add_to_cart:       { label: 'Add to Cart',        color: '#26a69a', icon: <AddShoppingCartOutlinedIcon fontSize="small" /> },
    initiate_checkout: { label: 'Initiate Checkout',  color: '#ffa726', icon: <PaymentOutlinedIcon fontSize="small" /> },
    purchase:          { label: 'Purchases',           color: '#ef5350', icon: <ShoppingBagOutlinedIcon fontSize="small" /> },
};
const EVENT_ORDER = ['pageview', 'add_to_cart', 'initiate_checkout', 'purchase'];

const fmtNum = (n: number | string) => Number(n).toLocaleString();
const pct = (num: number, den: number) => (!den ? '—' : ((num / den) * 100).toFixed(1) + '%');

const today = new Date().toISOString().split('T')[0];
const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

const PixelTrackingDashboard: React.FC = () => {
    const [loading, setLoading]       = useState(false);
    const [totals, setTotals]         = useState<Record<string, number>>({});
    const [brands, setBrands]         = useState<BrandRow[]>([]);
    const [startDate, setStartDate]   = useState(thirtyDaysAgo);
    const [endDate, setEndDate]       = useState(today);
    const [error, setError]           = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await PixelService.getPixelAnalytics({ start_date: startDate, end_date: endDate });
            const data = res?.data?.rows;
            if (!data) { setError('No data returned'); return; }
            const map: Record<string, number> = {};
            (data.totals as TotalRow[]).forEach(r => { map[r.event_type] = Number(r.total); });
            setTotals(map);
            setBrands(data.per_brand || []);
        } catch {
            setError('Failed to load analytics');
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => { load(); }, []);

    const pv  = totals['pageview']          || 0;
    const atc = totals['add_to_cart']      || 0;
    const ic  = totals['initiate_checkout']|| 0;
    const pur = totals['purchase']         || 0;

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>

            <AdminPageHead
                title="Pixel Tracking"
                subtitle="Events from brands' tracking pixels — views, add to cart, checkouts and purchases."
                actions={<>
                    <TextField
                        type="date" size="small" label="From" InputLabelProps={{ shrink: true }}
                        value={startDate} onChange={e => setStartDate(e.target.value)}
                        sx={{ width: 155 }}
                    />
                    <TextField
                        type="date" size="small" label="To" InputLabelProps={{ shrink: true }}
                        value={endDate} onChange={e => setEndDate(e.target.value)}
                        sx={{ width: 155 }}
                    />
                    <Button variant="contained" onClick={load} disabled={loading}>Apply</Button>
                    <Tooltip title="Refresh">
                        <IconButton onClick={load} size="small" disabled={loading}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </>}
            />

            {error && <Typography color="error" mb={2} variant="body2">{error}</Typography>}

            {/* 4 Metric cards */}
            <Grid container spacing={2} mb={2}>
                {EVENT_ORDER.map(et => {
                    const meta = EVENT_META[et];
                    const val  = totals[et] || 0;
                    return (
                        <Grid item xs={6} md={3} key={et}>
                            <Card variant="outlined">
                                <CardContent sx={{ pb: '12px !important' }}>
                                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                        <Box sx={{ color: meta.color, display: 'flex' }}>{meta.icon}</Box>
                                        <Typography variant="caption" color="text.secondary">{meta.label}</Typography>
                                    </Box>
                                    {loading
                                        ? <CircularProgress size={18} />
                                        : <Typography variant="h5" fontWeight={700} sx={{ color: meta.color }}>{fmtNum(val)}</Typography>
                                    }
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Funnel */}
            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="subtitle2" fontWeight={600} mb={2}>Conversion Funnel</Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                        {EVENT_ORDER.map((et, i) => {
                            const meta    = EVENT_META[et];
                            const val     = totals[et] || 0;
                            const prevVal = i > 0 ? (totals[EVENT_ORDER[i - 1]] || 0) : 0;
                            const dropPct = i > 0 ? pct(val, prevVal) : null;
                            const barW    = pv > 0 ? Math.max((val / pv) * 100, 3) : 3;
                            return (
                                <Box key={et} flex="1" minWidth={100}>
                                    <Box sx={{ height: 6, borderRadius: 3, background: meta.color, width: `${barW}%`, mb: 0.5 }} />
                                    <Typography variant="caption" color="text.secondary" display="block">{meta.label}</Typography>
                                    <Typography fontWeight={700} variant="body2">{fmtNum(val)}</Typography>
                                    {dropPct && (
                                        <Box display="flex" alignItems="center" gap={0.3}>
                                            <TrendingDownIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                                            <Typography variant="caption" color="text.disabled">{dropPct} of prev</Typography>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </CardContent>
            </Card>

            {/* Rate summary */}
            <Grid container spacing={2} mb={2}>
                {[
                    { label: 'View → Cart',            val: pct(atc, pv)  },
                    { label: 'Cart → Checkout',        val: pct(ic, atc)  },
                    { label: 'Checkout → Purchase',    val: pct(pur, ic)  },
                    { label: 'Overall Conversion',     val: pct(pur, pv)  },
                ].map(item => (
                    <Grid item xs={6} md={3} key={item.label}>
                        <Card variant="outlined" sx={{ background: '#fafafa' }}>
                            <CardContent sx={{ py: '10px !important' }}>
                                <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                                <Typography variant="h6" fontWeight={700}>{loading ? '—' : item.val}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Divider sx={{ mb: 2 }} />

            {/* Per-brand table */}
            <Typography variant="subtitle2" fontWeight={600} mb={1}>Per-Brand Breakdown</Typography>
            <TableContainer sx={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Table size="small" sx={{ minWidth: 750 }}>
                    <TableHead>
                        <TableRow sx={{ background: '#f5f5f5' }}>
                            <TableCell><strong>Pixel ID</strong></TableCell>
                            <TableCell><strong>Website</strong></TableCell>
                            <TableCell align="right"><strong>Page Views</strong></TableCell>
                            <TableCell align="right"><strong>Add to Cart</strong></TableCell>
                            <TableCell align="right"><strong>Checkout</strong></TableCell>
                            <TableCell align="right"><strong>Purchases</strong></TableCell>
                            <TableCell align="right"><strong>Unique Users</strong></TableCell>
                            <TableCell align="right"><strong>Conv. Rate</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        ) : brands.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    No pixel events in this period.
                                </TableCell>
                            </TableRow>
                        ) : brands.map(b => (
                            <TableRow key={b.cctm_id} hover>
                                <TableCell>
                                    <Chip label={b.cctm_id} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 11 }} />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 180, display: 'block' }}>
                                        {b.website_url || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">{fmtNum(b.pageviews)}</TableCell>
                                <TableCell align="right">{fmtNum(b.add_to_cart)}</TableCell>
                                <TableCell align="right">{fmtNum(b.initiate_checkout)}</TableCell>
                                <TableCell align="right">{fmtNum(b.purchases)}</TableCell>
                                <TableCell align="right">{fmtNum(b.unique_users)}</TableCell>
                                <TableCell align="right">
                                    <Typography
                                        variant="body2" fontWeight={600}
                                        sx={{ color: Number(b.purchases) > 0 ? '#2e7d32' : 'text.secondary' }}
                                    >
                                        {pct(Number(b.purchases), Number(b.pageviews))}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export { PixelTrackingDashboard };
